'use client';

import { BASE_URL, medusaClient } from '@lib/config';
import useToggleState, { StateType } from '@lib/hooks/use-toggle-state';
import {
    Address,
    Cart,
    Customer,
    StorePostCartsCartReq,
} from '@medusajs/medusa';
import Wrapper from '@modules/checkout/components/payment-wrapper';
import { isEqual } from 'lodash';
import {
    formatAmount,
    useCart,
    useCartShippingOptions,
    useMeCustomer,
    useSetPaymentSession,
    useUpdateCart,
} from 'medusa-react';
import { useRouter } from 'next/navigation';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useStore } from './store-context';
import { AddressPayload } from '@medusajs/medusa/dist/types/common';
import { AxiosError } from 'axios';
import { getPaymentSessionStatus, trackingForMarketing } from '@lib/data/order-confirm-handle';
import { toast } from 'react-toastify';

export type AddressValues = {
    first_name: string;
    last_name: string;
    company: string;
    address_1: string;
    address_2: string;
    city: string;
    province: string;
    postal_code: string;
    country_code: string;
    phone: string;
};

export type CheckoutFormValues = {
    shipping_address: AddressValues;
    billing_address: AddressValues;
    email: string;
};

export type fiservCard = {
    bin: string;
    brand: string;
    last4: string;
    name: string;
    token: string;
    masked: string;
    address1: string;
    address2: string;
    city: string;
    region: string;
    country: string;
    postalCode: string;
    company: string;
};

interface CheckoutContext {
    cart?: Omit<Cart, 'refundable_amount' | 'refunded_total'>;
    shippingMethods: { label?: string; value?: string; price: string }[];
    isLoading: boolean;
    readyToComplete: boolean;
    sameAsBilling: StateType;
    editAddresses: StateType;
    initPayment: () => Promise<void>;
    setAddresses: (addresses: CheckoutFormValues) => void;
    setSavedAddress: (address: Address) => void;
    setShippingOption: (soId: string) => void;
    setPaymentSession: (providerId: string) => void;
    onPaymentCompleted: (tracking?: boolean) => void;
    cartIsEmpty: boolean;
    setCartIsEmpty: (value: boolean) => void;
    stripePaymentMethodType?: string;
    setStripePaymentMethodType: (value: string) => void;
}

const CheckoutContext = createContext<CheckoutContext | null>(null);

interface CheckoutProviderProps {
    children?: React.ReactNode;
}

const IDEMPOTENCY_KEY = 'create_payment_session_key';

export const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
    const {
        cart,
        setCart,
        addShippingMethod: {
            mutate: setShippingMethod,
            isLoading: addingShippingMethod,
        },
        completeCheckout: { mutate: complete, isLoading: completingCheckout },
    } = useCart();

    const { customer } = useMeCustomer();
    const { isDeleteItemFromCart, setIsDeleteItemFromCart } = useStore();
    const { checkoutEmail, setCheckoutEmail } = useStore();

    const { countryCode, setLoading } = useStore();
    const [cartIsEmpty, setCartIsEmpty] = useState<boolean>(false);
    const [stripePaymentMethodType, setStripePaymentMethodType] = useState<string | undefined>();

    const methods = useForm<CheckoutFormValues>({
        defaultValues: mapFormValues(isDeleteItemFromCart, checkoutEmail, setIsDeleteItemFromCart, setCheckoutEmail, customer, cart, countryCode),
        reValidateMode: 'onChange',
    });

    const {
        mutate: setPaymentSessionMutation,
        isLoading: settingPaymentSession,
    } = useSetPaymentSession(cart?.id!);

    const { mutate: updateCart, isLoading: updatingCart } = useUpdateCart(
        cart?.id!
    );

    const { shipping_options } = useCartShippingOptions(cart?.id!, {
        enabled: !!cart?.id,
    });

    const { resetCart } = useStore();
    const { push } = useRouter();

    const editAddresses = useToggleState();
    const sameAsBilling = useToggleState(
        cart?.billing_address && cart?.shipping_address
            ? isEqual(cart.billing_address, cart.shipping_address)
            : true
    );

    /**
     * Boolean that indicates if a part of the checkout is loading.
     */
    const isLoading = useMemo(() => {
        return (
            addingShippingMethod ||
            settingPaymentSession ||
            updatingCart ||
            completingCheckout
        );
    }, [
        addingShippingMethod,
        completingCheckout,
        settingPaymentSession,
        updatingCart,
    ]);

    /**
     * Boolean that indicates if the checkout is ready to be completed. A checkout is ready to be completed if
     * the user has supplied a email, shipping address, billing address, shipping method, and a method of payment.
     */
    const readyToComplete = useMemo(() => {
        return (
            !!cart &&
            !!cart.email &&
            !!cart.shipping_address &&
            !!cart.billing_address &&
            !!cart.payment_session
        );
    }, [cart]);

    const shippingMethods = useMemo(() => {
        if (shipping_options && cart?.region) {
            return shipping_options?.map((option) => ({
                value: option.id,
                label: option.name,
                price: formatAmount({
                    amount: option.amount || 0,
                    region: cart.region,
                }),
            }));
        }

        return [];
    }, [shipping_options, cart]);

    useEffect(() => {
        if (!cart) {
            editAddresses.open();
            return;
        }

        if (cart?.shipping_address && cart?.billing_address) {
            editAddresses.close();
            return;
        }

        editAddresses.open();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart]);

    /**
     * Method to set the selected shipping method for the cart. This is called when the user selects a shipping method, such as UPS, FedEx, etc.
     */
    const setShippingOption = (soId: string) => {
        if (cart) {
            setShippingMethod(
                { option_id: soId },
                {
                    onSuccess: ({ cart }) => {
                        setCart(cart);
                    },
                }
            );
        }
    };

    /**
     * Method to create the payment sessions available for the cart. Uses a idempotency key to prevent duplicate requests.
     */
    const createPaymentSession = async (cartId: string) => {
        return medusaClient.carts
            .createPaymentSessions(cartId, {
                'Idempotency-Key': IDEMPOTENCY_KEY,
            })
            .then(({ cart }) => cart)
            .catch(() => null);
    };

    /**
     * Method that calls the createPaymentSession method and updates the cart with the payment session.
     */
    const initPayment = async () => {
        if (cart?.id && !cart.payment_sessions?.length && cart?.items?.length) {
            const paymentSession = await createPaymentSession(cart.id);

            if (!paymentSession) {
                setTimeout(initPayment, 500);
            } else {
                setCart(paymentSession);
                return;
            }
        }
    };

    /**
     * Method to set the selected payment session for the cart. This is called when the user selects a payment provider, such as Stripe, PayPal, etc.
     */
    const setPaymentSession = (providerId: string) => {
        if (cart) {
            setPaymentSessionMutation(
                {
                    provider_id: providerId,
                },
                {
                    onSuccess: ({ cart }) => {
                        setCart(cart);
                    },
                }
            );
        }
    };

    const prepareFinalSteps = () => {
        initPayment();

        if (
            shippingMethods?.length &&
            shippingMethods?.[0]?.value &&
            cart?.shipping_methods?.length == 0
        ) {
            setShippingOption(shippingMethods[0].value);
        }
    };

    const setSavedAddress = (address: Address) => {
        const setValue = methods.setValue;

        setValue('shipping_address', {
            address_1: address.address_1 || '',
            address_2: address.address_2 || '',
            city: address.city || '',
            country_code: address.country_code || '',
            first_name: address.first_name || '',
            last_name: address.last_name || '',
            phone: address.phone || '',
            postal_code: address.postal_code || '',
            province: address.province || '',
            company: address.company || '',
        });
        setValue('billing_address', {
            address_1: address.address_1 || '',
            address_2: address.address_2 || '',
            city: address.city || '',
            country_code: address.country_code || '',
            first_name: address.first_name || '',
            last_name: address.last_name || '',
            phone: address.phone || '',
            postal_code: address.postal_code || '',
            province: address.province || '',
            company: address.company || '',
        });
    };

    /**
     * Method that sets the addresses and email on the cart.
     */
    const setAddresses = (data: CheckoutFormValues) => {
        try {
            const { shipping_address, email } = data;

            let payload: StorePostCartsCartReq = {
                shipping_address,
                email,
                billing_address: shipping_address
            };
            const billingAddress = payload?.billing_address as AddressPayload;
            console.log('billingAddress', data);

            updateCart(payload, {
                onSuccess: ({ cart }) => {
                    cart.billing_address.company = billingAddress?.company || '';
                    cart.billing_address.first_name =
                        billingAddress?.first_name || '';
                    cart.billing_address.last_name = billingAddress?.last_name || '';
                    cart.billing_address.address_1 = billingAddress?.address_1 || '';
                    cart.billing_address.address_2 = billingAddress?.address_2 || '';
                    cart.billing_address.city = billingAddress?.city || '';
                    cart.billing_address.country_code =
                        billingAddress?.country_code || '';
                    cart.billing_address.province = billingAddress?.province || '';
                    cart.billing_address.postal_code =
                        billingAddress?.postal_code || '';
                    cart.billing_address.phone = billingAddress?.phone || '';
                    setCart(cart);
                    prepareFinalSteps();
                    console.log('prepareFinalSteps', cart);
                    setCheckoutEmail('');
                },
                onError: (error) => {
                    const message = (error as AxiosError)?.response?.data as string;
                    toast.error(<div dangerouslySetInnerHTML={{__html: message}}></div>);
                    console.error(message);
                    setLoading(false);
                    setCheckoutEmail('');
                },
            });

        } catch (error) {
            toast.error('Can not update billing address!, please try again.');
        }

    };
    const onPaymentErrorHandle = (error: Error) => {
        if (cart && error) {
            medusaClient.orders.retrieveByCartId(cart.id).then(
                ({ order }) => {
                    if (order) {
                        push(`/order/confirmed/${order.id}`);
                        setTimeout(() => {
                            resetCart();
                        }, 3000);
                        setLoading(false);
                    } // end if
                },
                (error) => {
                    console.error(
                        'Error inside order checking: ' + error.message
                    );
                    setLoading(false);
                }
            );
        } // end if
    }
    /**
     * Method to complete the checkout process. This is called when the user clicks the "Complete Checkout" button.
     */
    const onPaymentCompleted = (hasTracking?: boolean) => {
        complete(undefined, {
            onSuccess: async ({ data }) => {
                if (data.id?.indexOf('cart_') === -1) {
                    push(`${BASE_URL}/order/confirmed/${data.id}`);
                    setTimeout(() => {
                        resetCart();
                    }, 3000);
                } else {
                    setTimeout(async () => {
                        const res = await getPaymentSessionStatus(data.id);
                        const paymentStatus = res?.body?.data?.payment_status;
                        const orderId = res?.body?.data.order_id;
                        if (paymentStatus === 'authorized') {
                            push(`${BASE_URL}/order/confirmed/${orderId}`);
                        } else if (paymentStatus === 'pending') {
                            toast.error('Payment processing pending. Please wait');
                        }
                        setLoading(false);
                    }, 3000);
                }
                if (hasTracking) {
                    trackingForMarketing(data.id).then(() => { });
                }
            },
            onError: onPaymentErrorHandle
        });
    };

    const contextValue = useMemo(
        () => ({
            cart,
            shippingMethods,
            isLoading,
            readyToComplete,
            sameAsBilling,
            editAddresses,
            initPayment,
            setAddresses,
            setSavedAddress,
            setShippingOption,
            setPaymentSession,
            onPaymentCompleted,
            cartIsEmpty,
            setCartIsEmpty,
            stripePaymentMethodType,
            setStripePaymentMethodType
        }),
        [
            cart,
            shippingMethods,
            isLoading,
            readyToComplete,
            sameAsBilling.state,
            editAddresses.state,
            cartIsEmpty,
            stripePaymentMethodType
        ]
    );

    return (
        <FormProvider {...methods}>
            <CheckoutContext.Provider value={contextValue}>
                <Wrapper paymentSession={cart?.payment_session}>
                    {children}
                </Wrapper>
            </CheckoutContext.Provider>
        </FormProvider>
    );
};

export const useCheckout = () => {
    const context = useContext(CheckoutContext);
    const form = useFormContext<CheckoutFormValues>();
    if (context === null) {
        throw new Error(
            'useProductActionContext must be used within a ProductActionProvider'
        );
    }
    return { ...context, ...form };
};

/**
 * Method to map the fields of a potential customer and the cart to the checkout form values. Information is assigned with the following priority:
 * 1. Cart information
 * 2. Customer information
 * 3. Default values - null
 */
const mapFormValues = (
    isDeleteItemFromCart: boolean,
    checkoutEmail: string,
    setIsDeleteItemFromCart: (value: boolean) => void,
    setCheckoutEmail: (value: string) => void,
    customer?: Omit<Customer, 'password_hash'>,
    cart?: Omit<Cart, 'refundable_amount' | 'refunded_total'>,
    currentCountry?: string
): CheckoutFormValues => {
    const customerShippingAddress = customer?.shipping_addresses?.[0];
    const customerBillingAddress = customer?.billing_address;

    let email = customer?.email || cart?.email || '';
    const itemPrint = (cart?.items || [])
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
        .find(
            (item) =>
                (
                    (item.metadata.redemption_method as string) || ''
                ).toLowerCase() === 'print'
        );

    if (itemPrint && !customer) {
        email = itemPrint.metadata.sender_email as string;
    }

    if (isDeleteItemFromCart )
        email = checkoutEmail;

    const getShipingAddress = getAddress(
        isDeleteItemFromCart,
        cart?.shipping_address,
        customerShippingAddress,
        currentCountry
    )

    const getBillingAddress = getAddress(
        isDeleteItemFromCart,
        cart?.billing_address,
        customerBillingAddress
    )

    setTimeout(() => {
        if (isDeleteItemFromCart)
            setIsDeleteItemFromCart(false)
    }, 100);

    return {
        shipping_address: getShipingAddress,
        billing_address: getBillingAddress,
        email: email,
    };
};

const getAddress = (
    isDeleteItemFromCart: boolean,
    cartAddress?: Address | null,
    customerAddress?: Address | null,
    currentCountry?: string
) => {

    if (!isDeleteItemFromCart)
    {
        return {
            first_name: cartAddress?.first_name || customerAddress?.first_name || '',
            last_name: cartAddress?.last_name || customerAddress?.last_name || '',
            address_1: cartAddress?.address_1 || customerAddress?.address_1 || '',
            address_2: cartAddress?.address_2 || customerAddress?.address_2 || '',
            city: cartAddress?.city || customerAddress?.city || '',
            country_code:
                currentCountry ||
                cartAddress?.country_code ||
                customerAddress?.country_code ||
                '',
            province: cartAddress?.province || customerAddress?.province || '',
            company: cartAddress?.company || customerAddress?.company || '',
            postal_code: cartAddress?.postal_code || customerAddress?.postal_code || '',
            phone: cartAddress?.phone || customerAddress?.phone || '',
        } as AddressValues;
    }

    else {
        let address = {} as AddressValues;
        address.country_code = currentCountry || '';
        return address;
    } 
} 
